import { defineStore } from 'pinia';
import { ref } from 'vue';
import { getContactHistory, saveContactHistory, type IContactHistory } from '@/api/contactHistory';
import { showErrorToastMessage } from '@/utils/toastMessage';

const romeDay = (date: Date): string =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Rome' }).format(date);

/**
 * Keep only the entries started today (Europe/Rome). Entries whose
 * initiationTimestamp is not a parseable date (e.g. in-progress markers) are
 * kept so a running contact is never hidden.
 */
const isFromTodayInRome = ({ initiationTimestamp }: IContactHistory): boolean => {
  const parsed = new Date(initiationTimestamp);
  if (Number.isNaN(parsed.getTime())) {
    return true;
  }
  return romeDay(parsed) === romeDay(new Date());
};

export const useContactHistoryStore = defineStore('contactHistory', () => {
  const contactHistory = ref<IContactHistory[]>([]);
  const loading = ref(false);
  const error = ref<unknown>(null);

  const upsertContactHistory = (entry: IContactHistory): void => {
    const existingIndex = contactHistory.value.findIndex(({ contactId }) => contactId === entry.contactId);

    if (existingIndex === -1) {
      contactHistory.value.unshift(entry);
    } else {
      contactHistory.value[existingIndex] = entry;
    }
  };

  const saveContactHistoryEntry = async (entry: IContactHistory): Promise<void> => {
    console.log('[CONTACT HISTORY STORE] Saving entry:', entry);
    upsertContactHistory(entry);
    console.log('[CONTACT HISTORY STORE] Table updated locally:', contactHistory.value);
    await saveContactHistory(entry);
    upsertContactHistory(entry);
    console.log('[CONTACT HISTORY STORE] POST successful:', entry);
  };

  /**
   * FE-only update: used for the "In corso" row on contact entry, before the backend is involved.
   */
  const addOngoingContactHistoryEntry = (entry: IContactHistory): void => {
    upsertContactHistory(entry);
  };

  const loadContactHistory = async (agentUsername: string): Promise<void> => {
    if (!agentUsername) {
      contactHistory.value = [];
      return;
    }

    loading.value = true;
    error.value = null;

    try {
      const remoteHistory = await getContactHistory(agentUsername);
      const todaysHistory = remoteHistory.filter(isFromTodayInRome);
      // Keep today's local rows (ongoing or just closed) the remote has not returned yet,
      // e.g. a POST still in flight or not yet readable: the GET must not erase them.
      const localPendingEntries = contactHistory.value.filter((entry) =>
        entry.agentUsername.split('@')[0].toUpperCase() === agentUsername.split('@')[0].toUpperCase() &&
        isFromTodayInRome(entry)
      );
      const remoteContactIds = new Set(todaysHistory.map(({ contactId }) => contactId));

      contactHistory.value = [
        ...localPendingEntries.filter(({ contactId }) => !remoteContactIds.has(contactId)),
        ...todaysHistory
      ];
      console.log('[CONTACT HISTORY STORE] GET result merged into table:', contactHistory.value);
    } catch (requestError) {
      error.value = requestError;
      showErrorToastMessage('Il caricamento di storico contatti non è andata a buon fine');
      console.error('[CONTACT HISTORY STORE] GET failed; keeping local rows:', requestError);
    } finally {
      loading.value = false;
    }
  };

  return {
    contactHistory,
    loading,
    error,
    loadContactHistory,
    saveContactHistoryEntry,
    addOngoingContactHistoryEntry
  };
});
